import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { apiStateSelectors } from '../../../api/apiState'
import { reportingApiEvents } from 'modules/api/reporting'
import { userApiSelectors } from 'modules/api/user'
import { format, startOfMonth } from 'date-fns'
import { DateRangePicker, DimmerLoader, Header, Modal, Table } from '../../../../views/components'
import ReportingIcon from '../../../../views/components/atoms/Icons/Descriptive/ReportsFill'
import DownloadIcon from '../../../../views/components/atoms/Icons/Doc/DocumentDownload'
import styles from './AdminReportingTable.module.scss'
import { $TSFixMe } from 'types/ts-migrate'
import { useEffectOnce } from 'react-use'
import { agencyApiEvents } from 'modules/api/agency'

const dateFormat = 'yyyy-MM-dd'
const excelDataType = 'excel'
const csvDataType = 'text/csv'

const AdminReportingTable = () => {
  const dispatch = useDispatch()

  const [selectedReport, setSelectedReport] = useState<$TSFixMe>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isValidSupportStaff = useSelector(userApiSelectors.isValidSupportStaff)

  const defaultDates = {
    start: format(startOfMonth(Date.now()), dateFormat),
    finish: format(Date.now(), dateFormat),
  }

  useEffectOnce(() => {
    dispatch(agencyApiEvents.agencyListing_request())
  })

  const downloadReport = useCallback(
    action => {
      dispatch(action)
    },
    [dispatch],
  )

  const reports = [
    {
      id: 'franchisePerformance',
      name: 'Rawson Performance Report',
      dateParams: defaultDates,
      groupParams: {
        groupId: '754cceb6-f295-4d05-85c8-9d7ca4edeb1b',
      },
      action: reportingApiEvents.franchisePerformance_request,
      actionArgs: { dataType: excelDataType },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'royaltyReport',
      name: 'Fine Home Royalty Report',
      dateParams: defaultDates,
      groupParams: {
        groupId: 'e34dee56-6ac7-4848-87ae-240c78b0b084',
      },
      action: reportingApiEvents.royaltyReport_request,
      actionArgs: { dataType: excelDataType },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'reportUsage',
      name: 'Report Usage by Agency',
      dateParams: defaultDates,
      action: reportingApiEvents.reportUsage_request,
      actionArgs: {
        dataType: excelDataType,
        include_dashboard: false,
        source: 'db', // 'auto' | 'db' | 'logs', 'logs' has a limit of 30 days
      },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'activeAgencies',
      name: 'Active Agencies',
      action: reportingApiEvents.activeAgencies_request,
      actionArgs: { dataType: excelDataType },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'activeLeases',
      name: 'reOS Active Customers',
      dateParams: defaultDates,
      action: reportingApiEvents.activeLeases_request,
      actionArgs: { dataType: excelDataType },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'suspenseAccountAllocated',
      name: 'Suspense Account Allocated',
      dateParams: defaultDates,
      action: reportingApiEvents.suspenseAccountAllocated_request,
      actionArgs: { dataType: excelDataType },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'suspenseAccountUnallocated',
      name: 'Suspense Account Unallocated',
      dateParams: defaultDates,
      action: reportingApiEvents.suspenseAccountUnallocated_request,
      actionArgs: { dataType: excelDataType },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'suspenseAccountCsv',
      name: 'Suspense Account',
      dateParams: null,
      action: reportingApiEvents.suspenseAccountCsv_request,
      actionArgs: { dataType: csvDataType },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'walletNumbers',
      name: 'Wallet Numbers',
      dateParams: null,
      action: reportingApiEvents.wallets_request,
      actionArgs: { dataType: excelDataType },
      isVisible: isValidSupportStaff,
    },
  ].filter(item => !!item?.isVisible)

  const loadingEvents = reports.map(report => report.action)
  const isLoading = useSelector(state => apiStateSelectors.isLoading(state)(loadingEvents))

  const fileTypeLabel = selectedReport?.actionArgs?.dataType === csvDataType ? 'a CSV' : 'an Excel'

  return (
    <div className={styles.root}>
      <Header icon={<ReportingIcon />} text="Reporting" />
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Cell head>Report</Table.Cell>
            <Table.Cell head>&nbsp;</Table.Cell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {reports.map((report, i) => (
            <Table.Row key={`report-row-${i}`}>
              <Table.Cell>{report.name}</Table.Cell>
              <Table.Cell className={styles['action-cell']}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedReport(report)
                    setIsModalOpen(true)
                  }}
                >
                  <DownloadIcon />
                </button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Modal isOpen={isModalOpen} padding="sm" showClose={true} onClose={() => setIsModalOpen(false)}>
        <Modal.Header title={selectedReport?.name} />
        <Modal.Body
          actions={
            isLoading
              ? []
              : selectedReport?.groupParams
              ? [
                  {
                    label: 'Download',
                    onClick: () => {
                      downloadReport(
                        selectedReport?.action({
                          ...selectedReport.actionArgs,
                          ...selectedReport.dateParams,
                          ...selectedReport.groupParams,
                        }),
                      )
                    },
                  },
                ]
              : [
                  {
                    label: 'Download',
                    onClick: () => {
                      downloadReport(
                        selectedReport?.action({
                          ...selectedReport.actionArgs,
                          ...selectedReport.dateParams,
                        }),
                      )
                    },
                  },
                ]
          }
        >
          <DimmerLoader loading={isLoading} fadeStyle="flat" bgColor="white">
            <>
              {selectedReport?.dateParams?.start && selectedReport?.dateParams?.finish ? (
                <>
                  {!selectedReport?.propertyParams && (
                    <p>
                      Select a date range and click the button below to download {fileTypeLabel} version of this report.
                    </p>
                  )}
                  {selectedReport?.info && selectedReport.info}
                  <DateRangePicker
                    startLabel={selectedReport?.propertyParams ? 'Select a start date' : ''}
                    endLabel={selectedReport?.propertyParams ? 'Select a end date' : ''}
                    startDate={selectedReport.dateParams.start}
                    endDate={selectedReport.dateParams.finish}
                    onChange={(date: $TSFixMe, isStartDate: boolean) => {
                      isStartDate
                        ? setSelectedReport({
                            ...selectedReport,
                            dateParams: {
                              ...selectedReport.dateParams,
                              start: format(new Date(date), dateFormat),
                            },
                          })
                        : setSelectedReport({
                            ...selectedReport,
                            dateParams: {
                              ...selectedReport.dateParams,
                              finish: format(new Date(date), dateFormat),
                            },
                          })
                    }}
                    inline={false}
                  />
                </>
              ) : (
                <p>Click the button below to download {fileTypeLabel} version of this report.</p>
              )}
            </>
          </DimmerLoader>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default AdminReportingTable
